import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LogIn, Building2, Menu, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { LoginModal } from '@/components/LoginModal';
import { useAuth } from '@/contexts/AuthContext';
import { RegistrationChoice } from '@/components/registration/RegistrationChoice';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  /** Run edge to edge, to line up with full-width page content */
  fullWidth?: boolean;
}

export function Header({ fullWidth = false }: HeaderProps) {
  const { t, i18n } = useTranslation('navigation');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegistrationChoice, setShowRegistrationChoice] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // On /for-business the hash may already be #join, so navigating again wouldn't scroll.
  const goToJoin = () => {
    if (location.pathname === '/for-business') {
      document.getElementById('join')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/for-business#join');
    }
  };

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Filter out business users from public header auth state
  const isPublicUser = user && user.role !== 'business';
  const showAuthButtons = !user || (user && user.role === 'business');

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // People who ask the OS for less transparency or more contrast get the solid bar
  const [preferSolid, setPreferSolid] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-transparency: reduce), (prefers-contrast: more)');
    const update = () => setPreferSolid(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  const navItems = [
    { name: t('whatWeOffer'), path: '/what-we-offer' },
    { name: t('forBusiness'), path: '/for-business' },
    { name: t('about'), path: '/about' },
    { name: t('contact'), path: '/contact' },
  ];

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 border-b pt-[env(safe-area-inset-top)]",
        "transition-[background-color,border-color,box-shadow] duration-300 motion-reduce:transition-none",
        // Material: solid white by default; a frosted 80% white wherever the browser can blur.
        // 80% keeps nav text above WCAG AA even over the darkest hero frame.
        preferSolid
          ? "bg-background"
          : "bg-background supports-[backdrop-filter]:bg-background/80 supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150",
        isScrolled
          ? "border-border/80 shadow-[0_1px_12px_-2px_rgb(15_23_42/0.10)]"
          : "border-border/50 shadow-none"
      )}
    >
      <div className={cn("mx-auto px-4", !fullWidth && "max-w-7xl sm:px-6 lg:px-8")}>
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/brand/wildy-lockup-green.svg" alt="Wildy" className={cn("h-9 w-auto transition-transform duration-300 motion-reduce:transition-none", isScrolled ? "scale-95" : "scale-100")} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={cn(
                  "relative py-2 whitespace-nowrap text-foreground/75 transition-colors",
                  "hover:text-primary",
                  "after:absolute after:left-0 after:right-0 after:bottom-0 after:h-0.5 after:rounded-full after:transition-all after:duration-300",
                  isActivePath(item.path) ? (
                    "text-primary font-medium after:bg-primary after:w-full"
                  ) : (
                    "after:w-0 after:bg-primary hover:after:w-full"
                  )
                )}
              >
                {item.name}
              </Link>
            ))}
            <LanguageSwitcher variant="ghost" size="sm" />
            {showAuthButtons ? (
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  className="text-primary hover:bg-secondary hover:text-primary"
                  onClick={() => setShowLoginModal(true)}
                >
                  <LogIn className="mr-2 h-4 w-4" />
                  {t('login')}
                </Button>
                <Button
                  variant="outline"
                  className="border-primary text-primary bg-background hover:bg-secondary hover:text-primary"
                  onClick={() => goToJoin()}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  {t('registerBusiness')}
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                {isPublicUser && user.role === 'admin' && (
                  <Button
                    onClick={() => navigate('/business')}
                  >
                    Business Dashboard
                  </Button>
                )}
                {isPublicUser && (
                  <Button
                    variant="outline"
                    className="border-primary text-primary bg-background hover:bg-secondary hover:text-primary"
                    onClick={logout}
                  >
                    {t('logout')}
                  </Button>
                )}
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open menu"
              className="h-11 w-11 text-foreground hover:bg-secondary hover:text-primary"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent side="right" className="w-[300px] bg-background text-foreground pt-[max(1.5rem,env(safe-area-inset-top))]">
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center mb-8">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center">
                <img src="/brand/wildy-lockup-green.svg" alt="Wildy" className="h-9 w-auto" />
              </Link>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    "flex items-center min-h-[44px] px-3 rounded-lg text-lg transition-colors",
                    isActivePath(item.path)
                      ? "bg-secondary text-primary font-medium"
                      : "text-foreground/75 hover:bg-secondary hover:text-primary"
                  )}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4">
                <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4" aria-hidden />
                  {t('language')}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      i18n.changeLanguage('en').then(() => {
                        window.dispatchEvent(new Event('languageChanged'));
                      });
                    }}
                    className={cn(
                      "flex-1 min-h-[44px] px-4 rounded-lg text-sm font-medium transition-all duration-200 touch-manipulation flex items-center justify-center",
                      i18n.language === 'en' || i18n.language.startsWith('en')
                        ? "bg-primary text-white border-2 border-primary"
                        : "bg-background border-2 border-border text-foreground hover:border-primary/50 hover:bg-secondary"
                    )}
                    aria-pressed={i18n.language === 'en' || i18n.language.startsWith('en')}
                    aria-label="English"
                  >
                    {t('english')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      i18n.changeLanguage('es').then(() => {
                        window.dispatchEvent(new Event('languageChanged'));
                      });
                    }}
                    className={cn(
                      "flex-1 min-h-[44px] px-4 rounded-lg text-sm font-medium transition-all duration-200 touch-manipulation flex items-center justify-center",
                      i18n.language === 'es' || i18n.language.startsWith('es')
                        ? "bg-primary text-white border-2 border-primary"
                        : "bg-background border-2 border-border text-foreground hover:border-primary/50 hover:bg-secondary"
                    )}
                    aria-pressed={i18n.language === 'es' || i18n.language.startsWith('es')}
                    aria-label="Español"
                  >
                    {t('spanish')}
                  </button>
                </div>
              </div>
              {showAuthButtons ? (
                <div className="pt-4 space-y-3">
                  <Button
                    variant="ghost"
                    className="w-full min-h-[44px] text-primary hover:bg-secondary hover:text-primary"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setShowLoginModal(true);
                    }}
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    {t('login')}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full min-h-[44px] border-primary text-primary bg-background hover:bg-secondary hover:text-primary"
                    onClick={() => {
                      setIsMenuOpen(false);
                      goToJoin();
                    }}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    {t('registerBusiness')}
                  </Button>
                </div>
              ) : (
                <div className="pt-4 space-y-3">
                  {isPublicUser && user.role === 'admin' && (
                    <Button
                      className="w-full min-h-[44px]"
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/business');
                      }}
                    >
                      Business Dashboard
                    </Button>
                  )}
                  {isPublicUser && (
                    <Button
                      variant="outline"
                      className="w-full min-h-[44px] border-primary text-primary bg-background hover:bg-secondary hover:text-primary"
                      onClick={() => {
                        setIsMenuOpen(false);
                        logout();
                      }}
                    >
                      {t('logout')}
                    </Button>
                  )}
                </div>
              )}
            </nav>
          </div>
        </SheetContent>
      </Sheet>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onRegisterBusiness={() => {
          setShowLoginModal(false);
          goToJoin();
        }}
      />

      <RegistrationChoice
        isOpen={showRegistrationChoice}
        onClose={() => setShowRegistrationChoice(false)}
      />
    </header>
  );
}